import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarPlus, Loader2, Search, Square, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { API } from "@/App";

// Panel admin > Vanta AI : les abonnés (Pro, Max), avec prolonger de N jours,
// passer de Pro à Max (ou l'inverse) et arrêter l'offre. Un abonnement payé par
// Stripe arrêté ici n'est plus prélevé.

const PLAN = {
  pro: ["Pro", "border-blue-500/40 bg-blue-500/10 text-blue-200"],
  max: ["Max", "border-violet-500/40 bg-violet-500/10 text-violet-200"],
  free: ["Terminé", "border-white/10 bg-white/5 text-white/45"],
};
const errorText = (err, fallback) => err?.response?.data?.detail || fallback;
const date = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : "");

function Row({ sub, act }) {
  const [days, setDays] = useState(30);
  const [plan, cls] = PLAN[sub.plan] || PLAN.free;
  return (
    <div className="px-3 py-3 flex flex-wrap items-center gap-x-4 gap-y-2" data-testid={`vai-sub-${sub.email}`}>
      <div className="flex-1 min-w-[200px]">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">{sub.username || sub.email}</span>
          <span className={`text-[11px] px-1.5 py-0.5 rounded border ${cls}`}>{plan}</span>
          <span className="text-[11px] text-white/40">{sub.source === "stripe" ? "payé (Stripe)" : "offert"}</span>
        </div>
        <p className="text-xs text-white/45 mt-0.5">
          {sub.email} · {sub.active ? `jusqu'au ${date(sub.ends_at)} (${sub.days_left} j)` : `fini le ${date(sub.ends_at)}`}
          {sub.active && sub.cancel_at_period_end ? " · ne se renouvelle pas" : ""}
        </p>
        {sub.history.length > 0 && <p className="text-[11px] text-white/30 mt-0.5 truncate" title={sub.history.map((h) => `${date(h.at)} ${h.text} (${h.by})`).join("\n")}>Dernière action : {sub.history[sub.history.length - 1].text}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <Input type="number" min={1} max={3650} value={days} onChange={(e) => setDays(e.target.value)} className="w-20 h-8 text-xs" aria-label="Jours" />
        <Button size="sm" variant="outline" className="h-8" onClick={() => act(sub, "extend", { days: Number(days) })} data-testid={`vai-sub-extend-${sub.email}`}>
          <CalendarPlus className="w-3.5 h-3.5 mr-1" />Prolonger
        </Button>
        {sub.active && (
          <select value={sub.plan} onChange={(e) => act(sub, "plan", { plan: e.target.value }, true)} className="h-8 rounded-md bg-transparent border border-white/15 px-2 text-xs" aria-label="Offre">
            <option value="pro" className="bg-[#14151c]">Pro</option>
            <option value="max" className="bg-[#14151c]">Max</option>
          </select>
        )}
        {sub.active && (
          <Button size="sm" variant="ghost" className="h-8 text-red-300" onClick={() => act(sub, "end", {}, true)} data-testid={`vai-sub-end-${sub.email}`}>
            <Square className="w-3.5 h-3.5 mr-1" />Arrêter
          </Button>
        )}
      </div>
    </div>
  );
}

export default function VantaAISubscribers({ hdr, reloadKey, onChanged }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [all, setAll] = useState(false);

  const load = useCallback(() => axios.get(`${API}/admin/vai/subscriptions`, { headers: hdr(), params: { q, status: all ? "all" : "active" } })
    .then((r) => setRows(r.data.subscriptions)).catch((err) => toast.error(errorText(err, "Chargement impossible"))), [hdr, q, all]);
  useEffect(() => { const t = setTimeout(load, q ? 300 : 0); return () => clearTimeout(t); }, [load, q, reloadKey]);

  // Arrêter ou changer une offre : confirmation claire d'abord (pas de fenêtre du navigateur).
  const [pending, setPending] = useState(null);
  const [working, setWorking] = useState(false);
  const act = async (sub, action, body = {}, confirm = false) => {
    if (confirm) { setPending({ sub, action, body }); return; }
    try {
      await axios.post(`${API}/admin/vai/subscriptions/${sub.user_id}/${action}`, body, { headers: hdr() });
      toast.success({ extend: `+${body.days} jour(s) pour ${sub.email}`, plan: "Offre changée", end: "Offre arrêtée" }[action]);
      load();
      onChanged?.();
    } catch (err) { toast.error(errorText(err, "Action impossible")); }
  };
  const confirmPending = async () => {
    setWorking(true);
    await act(pending.sub, pending.action, pending.body);
    setWorking(false);
    setPending(null);
  };
  const p = pending;
  const ending = p?.action === "end";
  const planName = { pro: "Pro", max: "Max" };

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-3" data-testid="vai-subscribers">
      <AlertDialog open={!!p} onOpenChange={(open) => { if (!open && !working) setPending(null); }}>
        <AlertDialogContent className="bg-[#12121a] border-white/10" data-testid="vai-sub-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{ending ? "Arrêter cette offre ?" : `Passer à ${planName[p?.body?.plan] || ""} ?`}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-white/65">
                <p><span className="text-white font-medium">{p?.sub.username || p?.sub.email}</span> ({p?.sub.email})</p>
                {ending ? (
                  <>
                    <p>Son offre {planName[p?.sub.plan] || ""} s'arrête tout de suite : il repasse en Gratuit.</p>
                    {p?.sub.source === "stripe"
                      ? <p className="text-amber-200">Abonnement payé : le prochain prélèvement Stripe est annulé. Le mois en cours n'est pas remboursé automatiquement.</p>
                      : <p>Offre offerte : rien n'est facturé.</p>}
                  </>
                ) : (
                  <p>Le changement est immédiat, jusqu'à la même date de fin. {p?.sub.source === "stripe" ? "Le prix Stripe ne change pas." : ""}</p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={working}>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmPending(); }} disabled={working} data-testid="vai-sub-confirm-ok"
              className={ending ? "bg-red-600 hover:bg-red-500 text-white" : ""}>
              {working ? <Loader2 className="w-4 h-4 animate-spin" /> : ending ? "Arrêter l'offre" : "Changer d'offre"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium flex items-center gap-2"><Users className="w-4 h-4" />Abonnés</p>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2 text-white/30" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Email ou pseudo" className="pl-8 h-8 w-52 text-xs" />
          </div>
          <label className="flex items-center gap-1.5 text-xs text-white/60">
            <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />Aussi les offres finies
          </label>
        </div>
      </div>
      {rows === null ? <p className="text-sm text-white/40 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Chargement…</p>
        : rows.length === 0 ? <p className="text-sm text-white/40">Aucun abonné{q ? " trouvé" : " pour l'instant"}.</p> : (
          <div className="divide-y divide-white/5 border border-white/10 rounded-lg">
            {rows.map((s) => <Row key={s.user_id} sub={s} act={act} />)}
          </div>
        )}
    </div>
  );
}
